import { useRef, useState, useEffect } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { WorkflowNode } from './WorkflowNode';
import { CanvasToolbar } from './CanvasToolbar';
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from '@/components/ui/context-menu';
import { BLOCK_REGISTRY } from '@/services/blocks/registry';
import { getBlockTypeLabel } from '@/lib/blockTypes';
import { initializePlacementContext, SWISS_ARMY_BLOCK_HELP } from '@/lib/blockContext';
import { getBrandedBlockDef } from '@/lib/brandedBlocks';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { toast } from 'sonner';
import { missionStartBlocks } from '@/lib/missionRules';

const NODE_TEMPLATES = [
  ...BLOCK_REGISTRY.map((b) => ({
    type: b.category,
    subtype: b.subtype,
    label: b.label,
  })),
  { type: 'trigger' as const, subtype: 'api' as const, label: getBlockTypeLabel('trigger', 'api') },
];

export function Canvas() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const { currentWorkflow, addNode, selectNode, selectedNode, deleteNode } = useWorkflowStore();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [zoom, setZoom] = useState(100);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Delete selected node
      if (e.key === 'Delete' && selectedNode) {
        deleteNode(selectedNode);
      }
      // Deselect node with Escape
      if (e.key === 'Escape') {
        selectNode(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode, deleteNode, selectNode]);

  const handleAddNode = (template: (typeof NODE_TEMPLATES)[number]) => {
    if (!canvasRef.current || !currentWorkflow) return;

    if (template.type === 'start' && missionStartBlocks(currentWorkflow).length > 0) {
      toast.message('This Mission already has a START block');
      setContextMenu(null);
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const x = contextMenu ? contextMenu.x - rect.left : 50;
    const y = contextMenu ? contextMenu.y - rect.top : 50;

    let defaultConfig: Record<string, unknown> = initializePlacementContext(
      currentWorkflow.id,
      template.type,
      template.subtype,
      template.label
    );

    if (template.type === 'start' && template.subtype === 'mission') {
      defaultConfig = { ...defaultConfig, name: 'Start' };
    } else if (template.type === 'goal' && template.subtype === 'outcome') {
      defaultConfig = { ...defaultConfig, outcome: '', name: 'Identified outcome' };
    } else if (template.type === 'agent' && template.subtype === 'assistant') {
      defaultConfig = { ...defaultConfig, agentBrief: '', autonomy: 'supervised' };
    } else if (template.type === 'drone' && template.subtype === 'remote') {
      defaultConfig = { ...defaultConfig, droneTask: '', remoteTarget: '' };
    } else if (template.type === 'branded') {
      const branded = getBrandedBlockDef(template.subtype);
      defaultConfig = {
        ...defaultConfig,
        brand: branded?.brand,
        ...(branded?.defaultConfig ?? {}),
      };
    }

    addNode({
      id: Math.random().toString(36).substr(2, 9),
      type: template.type,
      subtype: template.subtype,
      config: defaultConfig,
      x,
      y,
    });

    setContextMenu(null);
  };

  const hasStart = currentWorkflow ? missionStartBlocks(currentWorkflow).length > 0 : false;

  const handleZoomIn = () => setZoom((z) => Math.min(z + 10, 200));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 10, 50));
  if (!currentWorkflow) {
    return (
      <div className="h-full min-h-0 flex items-center justify-center bg-muted/30 rounded-lg border border-border">
        <div className="text-center space-y-3">
          <p className="text-muted-foreground text-lg font-medium">
            No {PRODUCT_TERMS.autoBlockFlow.toLowerCase()} selected
          </p>
          <p className="text-muted-foreground text-sm">
            Choose a flow from the selector in the run bar, or plan one in the{' '}
            {PRODUCT_TERMS.autoBlockFlowComposer}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={canvasRef}
          role="application"
          aria-label={PRODUCT_TERMS.mission}
          className="h-full min-h-0 relative bg-gradient-to-br from-background to-muted/20 rounded-lg border border-border overflow-auto"
          style={{ zoom: `${zoom}%` }}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (!target.closest('[data-workflow-node]')) {
              selectNode(null);
            }
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            setContextMenu({ x: e.clientX, y: e.clientY });
          }}
        >
          <CanvasToolbar onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} zoom={zoom} />
          {/* Grid background */}
          <svg
            className="absolute inset-0 w-full h-full opacity-10 pointer-events-none"
            width="100%"
            height="100%"
          >
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>

          {/* Edges (connection lines) */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" width="100%" height="100%">
            {currentWorkflow.edges.map((edge) => {
              const fromNode = currentWorkflow.nodes.find((n) => n.id === edge.from);
              const toNode = currentWorkflow.nodes.find((n) => n.id === edge.to);
              if (!fromNode || !toNode) return null;

              const isJourneyTube = edge.kind === 'journey_tube';
              const x1 = fromNode.x + 96;
              const y1 = fromNode.y + 40;
              const x2 = toNode.x;
              const y2 = toNode.y + 40;
              const midX = (x1 + x2) / 2;
              const midY = (y1 + y2) / 2;

              if (isJourneyTube) {
                return (
                  <g key={edge.id}>
                    <path
                      d={`M ${x1} ${y1} Q ${midX} ${midY - 24} ${x2} ${y2}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      className="text-primary opacity-60"
                      strokeLinecap="round"
                    />
                    <text
                      x={midX}
                      y={midY - 30}
                      textAnchor="middle"
                      className="fill-primary text-[9px] font-medium opacity-80"
                    >
                      Journey Tube
                    </text>
                  </g>
                );
              }

              return (
                <line
                  key={edge.id}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-primary opacity-50"
                  strokeDasharray="5,5"
                />
              );
            })}
          </svg>

          {/* Nodes */}
          <div className="absolute inset-0">
            {currentWorkflow.nodes.map((node) => (
              <WorkflowNode key={node.id} node={node} />
            ))}
          </div>

          {/* Empty state */}
          {currentWorkflow.nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-6 text-center">
              <p className="text-muted-foreground text-sm max-w-sm">
                {SWISS_ARMY_BLOCK_HELP} Right-click to place blocks — agents, drones, tools, and more.
              </p>
            </div>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        {!hasStart && (
          <>
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
                START {PRODUCT_TERMS.blockTypes.toUpperCase()}
              </p>
              {NODE_TEMPLATES.filter((t) => t.type === 'start').map((template) => (
                <button
                  key={template.subtype}
                  onClick={() => handleAddNode(template)}
                  className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent hover:text-accent-foreground cursor-pointer"
                >
                  {template.label}
                </button>
              ))}
            </div>
            <div className="my-2 h-px bg-border" />
          </>
        )}
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
            GOAL {PRODUCT_TERMS.blockTypes.toUpperCase()}
          </p>
          {NODE_TEMPLATES.filter((t) => t.type === 'goal').map((template) => (
            <button
              key={template.subtype}
              onClick={() => handleAddNode(template)}
              className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              {template.label}
            </button>
          ))}
        </div>
        <div className="my-2 h-px bg-border" />
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
            AGENT {PRODUCT_TERMS.blockTypes.toUpperCase()}
          </p>
          {NODE_TEMPLATES.filter((t) => t.type === 'agent').map((template) => (
            <button
              key={template.subtype}
              onClick={() => handleAddNode(template)}
              className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              {template.label}
            </button>
          ))}
        </div>
        <div className="my-2 h-px bg-border" />
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
            DRONE {PRODUCT_TERMS.blockTypes.toUpperCase()}
          </p>
          {NODE_TEMPLATES.filter((t) => t.type === 'drone').map((template) => (
            <button
              key={template.subtype}
              onClick={() => handleAddNode(template)}
              className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              {template.label}
            </button>
          ))}
        </div>
        <div className="my-2 h-px bg-border" />
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
            BRANDED {PRODUCT_TERMS.blockTypes.toUpperCase()}
          </p>
          <p className="text-[10px] text-muted-foreground px-2 pb-1">
            Ideas — Director builds when purpose is clear
          </p>
          {NODE_TEMPLATES.filter((t) => t.type === 'branded').map((template) => (
            <button
              key={template.subtype}
              onClick={() => handleAddNode(template)}
              className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              {template.label}
            </button>
          ))}
        </div>
        <div className="my-2 h-px bg-border" />
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
            TRIGGER {PRODUCT_TERMS.blockTypes.toUpperCase()}
          </p>
          {NODE_TEMPLATES.filter((t) => t.type === 'trigger').map((template) => (
            <button
              key={template.subtype}
              onClick={() => handleAddNode(template)}
              className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              {template.label}
            </button>
          ))}
        </div>
        <div className="my-2 h-px bg-border" />
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
            TOOL {PRODUCT_TERMS.blockTypes.toUpperCase()}
          </p>
          {NODE_TEMPLATES.filter((t) => t.type === 'tool').map((template) => (
            <button
              key={template.subtype}
              onClick={() => handleAddNode(template)}
              className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              {template.label}
            </button>
          ))}
        </div>
        <div className="my-2 h-px bg-border" />
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
            ACTION {PRODUCT_TERMS.blockTypes.toUpperCase()}
          </p>
          {NODE_TEMPLATES.filter((t) => t.type === 'action').map((template) => (
            <button
              key={template.subtype}
              onClick={() => handleAddNode(template)}
              className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              {template.label}
            </button>
          ))}
        </div>
      </ContextMenuContent>
    </ContextMenu>
  );
}